import { PageHeader, Stat, Stats } from '../../components/office';
import { Signal } from '@verlo/shared/components/ui/signal';
import { Notice } from '@verlo/shared/components/ui/notice';
import { useState, useEffect } from 'react';
import { labourApi, reportsApi } from '@verlo/shared/services/api';
import { Button } from '@verlo/shared/components/ui/button';
import { Skeleton } from '@verlo/shared/components/ui/skeleton';
import { Switch } from '@verlo/shared/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@verlo/shared/components/ui/select';
import { toast } from 'sonner';
import { RefreshCw } from 'lucide-react';

const PnLOverviewPage = () => {
  const [pnlData, setPnlData] = useState(null);
  const [yoyData, setYoyData] = useState(null);
  const [categoryData, setCategoryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');
  const [showYoY, setShowYoY] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchData();
  }, [dateRange, showYoY, selectedCategory]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - parseInt(dateRange) * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      const res = await labourApi.getOperatingPnl(startDate, endDate);
      setPnlData(res.data);
      
      // Fetch YoY data if enabled
      if (showYoY) {
        try {
          const yoyRes = await reportsApi.getYoYComparison(startDate, endDate);
          setYoyData(yoyRes.data);
        } catch (e) {
          console.log('YoY data not available');
        }
      }
      
      // Fetch category data
      try {
        const catRes = await reportsApi.getSalesByCategory(startDate, endDate, selectedCategory !== 'all' ? selectedCategory : null, parseInt(dateRange));
        setCategoryData(catRes.data);
      } catch (e) {
        console.log('Category data not available');
      }
    } catch (err) {
      toast.error("Couldn't load P&L data");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value || 0);
  };

  const getStatusColor = (margin) => {
    if (margin >= 20) return 'text-signal-ok';
    if (margin >= 10) return 'text-signal-warn';
    return 'text-signal-stop';
  };

  const tone = (margin) => (margin >= 20 ? 'ok' : margin >= 10 ? 'warn' : 'stop');
  const bar = (margin) => (margin >= 20 ? 'bg-signal-ok-line' : margin >= 10 ? 'bg-signal-warn-line' : 'bg-signal-stop-line');
  const section = 'rounded-lg border border-hairline bg-white p-5';
  const h2 = 'text-[16px] font-semibold text-ink';
  const yoyDelta = (v) => (
    <span className={`font-mono tabular-nums ${v >= 0 ? 'text-signal-ok' : 'text-signal-stop'}`}>{v >= 0 ? '+' : '−'}{Math.abs(v)}%</span>
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24 rounded-lg" />)}
        </div>
        <Skeleton className="h-64 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pnl-overview-page">
      <PageHeader
        title="Operating P&L"
        purpose="Revenue less cost of sales and labour: what the month made."
        actions={(<>
          <label className="flex items-center gap-2 text-sm text-ink-soft" htmlFor="yoy-toggle">
            <Switch id="yoy-toggle" checked={showYoY} onCheckedChange={setShowYoY} data-testid="yoy-toggle" />
            Year on year
          </label>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              <SelectItem value="starters">Starters</SelectItem>
              <SelectItem value="mains">Mains</SelectItem>
              <SelectItem value="burgers">Burgers</SelectItem>
              <SelectItem value="desserts">Desserts</SelectItem>
              <SelectItem value="wines">Wine</SelectItem>
              <SelectItem value="cocktails">Cocktails</SelectItem>
              <SelectItem value="lagers">Lagers</SelectItem>
              <SelectItem value="soft_drinks">Soft drinks</SelectItem>
            </SelectContent>
          </Select>
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={fetchData} aria-label="Refresh"><RefreshCw size={16} /></Button>
        </>)}
      />

      {showYoY && yoyData && (
        <div className={`${section} flex flex-wrap items-center gap-x-8 gap-y-2 py-4`} data-testid="pnl-yoy">
          <div className="min-w-0">
            <div className="text-sm font-medium text-ink">Year on year</div>
            <div className="text-xs text-ink-faint">{yoyData.period?.current?.label} against {yoyData.period?.previous?.label}</div>
          </div>
          <div className="flex gap-8 text-sm">
            <span className="flex flex-col"><span className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-faint">Revenue</span>{yoyDelta(yoyData.variance?.revenue)}</span>
            <span className="flex flex-col"><span className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-faint">Orders</span>{yoyDelta(yoyData.variance?.order_count)}</span>
            <span className="flex flex-col"><span className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-faint">Average order</span>{yoyDelta(yoyData.variance?.avg_order_value)}</span>
          </div>
        </div>
      )}

      {/* Costs first, then what came in, then what's left (Martin, Oct 2026). The figure is
          the tile: no icons. */}
      <Stats>
        <Stat label="Cost of goods" value={formatCurrency(pnlData?.cogs)} hint={`${pnlData?.breakdown?.cogs_percentage ?? 0}% of revenue`} testId="pnl-cogs" />
        <Stat label="Labour cost" value={formatCurrency(pnlData?.labour_cost)} hint={`${pnlData?.breakdown?.labour_percentage ?? 0}% of revenue`} testId="pnl-labour" />
        <Stat label="Revenue" value={formatCurrency(pnlData?.revenue)} hint="100% of revenue" testId="pnl-revenue" />
        <Stat label="Operating profit" value={formatCurrency(pnlData?.operating_profit)}
          delta={{ text: `${pnlData?.operating_margin ?? 0}% margin`, tone: tone(pnlData?.operating_margin) }} testId="pnl-profit" />
      </Stats>

      <div className="grid gap-6 md:grid-cols-2">
        <section className={section} data-testid="pnl-breakdown">
          <h2 className={h2}>P&amp;L breakdown</h2>
          <div className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-sm"><span className="text-ink-soft">Revenue</span><span className="font-mono font-semibold text-ink">{formatCurrency(pnlData?.revenue)}</span></div>
              <div className="h-2.5 rounded-full bg-verlo" />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-sm"><span className="text-ink-soft">Less cost of goods</span><span className="font-mono text-ink-soft">−{formatCurrency(pnlData?.cogs)}</span></div>
              <div className="h-2.5 rounded-full bg-verlo/40" style={{ width: `${pnlData?.breakdown?.cogs_percentage || 0}%` }} />
            </div>
            <div className="space-y-1.5 border-t border-hairline pt-3">
              <div className="flex items-center justify-between text-sm"><span className="text-ink">Gross profit</span><span className="font-mono font-semibold text-verlo">{formatCurrency(pnlData?.gross_profit)}</span></div>
              <div className="h-2.5 rounded-full bg-verlo/70" style={{ width: `${pnlData?.gross_margin || 0}%` }} />
              <p className="text-xs text-ink-faint">{pnlData?.gross_margin}% gross margin</p>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-sm"><span className="text-ink-soft">Less labour</span><span className="font-mono text-ink-soft">−{formatCurrency(pnlData?.labour_cost)}</span></div>
              <div className="h-2.5 rounded-full bg-verlo/30" style={{ width: `${pnlData?.breakdown?.labour_percentage || 0}%` }} />
            </div>
            <div className="space-y-1.5 border-t border-hairline-strong pt-3">
              <div className="flex items-center justify-between text-sm"><span className="font-semibold text-ink">Operating profit</span><span className={`font-mono font-semibold ${getStatusColor(pnlData?.operating_margin)}`}>{formatCurrency(pnlData?.operating_profit)}</span></div>
              <div className={`h-2.5 rounded-full ${bar(pnlData?.operating_margin)}`} style={{ width: `${Math.max(0, pnlData?.operating_margin || 0)}%` }} />
              <p className={`text-xs ${getStatusColor(pnlData?.operating_margin)}`}>{pnlData?.operating_margin}% operating margin</p>
            </div>
          </div>
        </section>

        <section className={section} data-testid="pnl-distribution">
          <h2 className={h2}>Where the money goes</h2>
          <div className="mt-4 space-y-5">
            <div className="flex h-3 overflow-hidden rounded-full bg-paper">
              <div className="bg-signal-warn-line" style={{ width: `${pnlData?.breakdown?.cogs_percentage || 0}%` }} title={`Cost of goods: ${pnlData?.breakdown?.cogs_percentage}%`} />
              <div className="bg-signal-info-line" style={{ width: `${pnlData?.breakdown?.labour_percentage || 0}%` }} title={`Labour: ${pnlData?.breakdown?.labour_percentage}%`} />
              <div className={bar(pnlData?.operating_margin)} style={{ width: `${Math.max(0, pnlData?.breakdown?.profit_percentage || 0)}%` }} title={`Profit: ${pnlData?.breakdown?.profit_percentage}%`} />
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div><div className="flex items-center gap-1.5 text-ink-soft"><i className="h-2 w-2 rounded-full bg-signal-warn-line" />Cost of goods</div><div className="mt-0.5 font-mono text-lg text-ink">{pnlData?.breakdown?.cogs_percentage}%</div></div>
              <div><div className="flex items-center gap-1.5 text-ink-soft"><i className="h-2 w-2 rounded-full bg-signal-info-line" />Labour</div><div className="mt-0.5 font-mono text-lg text-ink">{pnlData?.breakdown?.labour_percentage}%</div></div>
              <div><div className="flex items-center gap-1.5 text-ink-soft"><i className={`h-2 w-2 rounded-full ${bar(pnlData?.operating_margin)}`} />Profit</div><div className={`mt-0.5 font-mono text-lg ${getStatusColor(pnlData?.operating_margin)}`}>{pnlData?.breakdown?.profit_percentage}%</div></div>
            </div>
            {pnlData?.wastage > 0 && (
              <Notice tone="stop" word="Waste" title={`${formatCurrency(pnlData.wastage)} lost to waste this period`}
                      action={<Signal tone="stop">{((pnlData.wastage / pnlData.revenue) * 100).toFixed(1)}% of revenue</Signal>} />
            )}
            <div className="border-t border-hairline pt-3 text-sm">
              <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-faint">Against typical targets</div>
              <div className="space-y-1.5">
                <div className="flex justify-between"><span className="text-ink-soft">Cost of goods, 25–30%</span><span className={pnlData?.breakdown?.cogs_percentage <= 30 ? 'text-signal-ok' : 'text-signal-warn'}>{pnlData?.breakdown?.cogs_percentage}%</span></div>
                <div className="flex justify-between"><span className="text-ink-soft">Labour, 25–30%</span><span className={pnlData?.breakdown?.labour_percentage <= 30 ? 'text-signal-ok' : 'text-signal-warn'}>{pnlData?.breakdown?.labour_percentage}%</span></div>
                <div className="flex justify-between"><span className="text-ink-soft">Operating margin, 15–25%</span><span className={pnlData?.operating_margin >= 15 ? 'text-signal-ok' : 'text-signal-warn'}>{pnlData?.operating_margin}%</span></div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {categoryData && (
        <section className={section} data-testid="pnl-categories">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className={h2}>Sales by category</h2>
            <div className="flex gap-2">
              <Signal tone="info">Wet {categoryData?.totals?.wet_percentage}%</Signal>
              <Signal tone="ok">Dry {categoryData?.totals?.dry_percentage}%</Signal>
            </div>
          </div>
          <div className="mt-4 space-y-2">
            {categoryData?.by_category?.slice(0, 8).map((cat, idx) => (
              <div key={idx} className="flex items-center gap-3 text-sm">
                <div className="w-28 truncate text-ink-soft">{cat.display_name}</div>
                <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-paper">
                  <div className={`h-full ${cat.type === 'wet' ? 'bg-signal-info-line' : 'bg-signal-ok-line'}`} style={{ width: `${cat.percentage_of_total}%` }} />
                </div>
                <div className="w-24 text-right font-mono text-ink">{formatCurrency(cat.revenue)}</div>
                <div className={`w-14 text-right font-mono ${cat.margin >= 50 ? 'text-signal-ok' : cat.margin >= 30 ? 'text-signal-warn' : 'text-signal-stop'}`}>{cat.margin}%</div>
              </div>
            ))}
          </div>
          {categoryData?.top_items?.length > 0 && (
            <div className="mt-5 border-t border-hairline pt-4">
              <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-faint">Top sellers</div>
              <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
                {categoryData?.top_items?.slice(0, 5).map((item, idx) => (
                  <div key={idx} className="rounded-md border border-hairline p-2.5">
                    <p className="truncate text-xs text-ink-soft">{item.name}</p>
                    <p className="font-mono text-lg text-ink">{item.quantity}</p>
                    <p className="font-mono text-xs text-ink-faint">{formatCurrency(item.revenue)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default PnLOverviewPage;
